/**
 * +field: where the game opens (pure; main.ts acts on it).
 *  - `?map=<mapId>&lvl=<n>&class=<id>` (dev / screenshots): a throwaway offline character straight
 *    onto that map (never written to the real save);
 *  - no character yet (new player, or a migrated v4 save): character creation, the old main class
 *    preselected;
 *  - else the character's saved map and position.
 */
import type { Profile } from '@keystone/progression';

export interface DebugBoot {
  mapId: string;
  level: number;
  classId: string;
  name: string;
}

export type BootRoute =
  | { kind: 'create'; preselect: string }
  | { kind: 'world'; mapId: string; pos: { x: number; y: number } | null; facing: number }
  | { kind: 'debug'; boot: DebugBoot };

export interface BootEnv {
  /** Map ids that exist (DungeonDef ids). */
  hasMap(id: string): boolean;
  /** Playable class ids. */
  classes: readonly string[];
  /** Where a character with an unknown map goes. */
  homeMap: string;
}

export function parseDebugBoot(params: URLSearchParams, env: BootEnv): DebugBoot | null {
  const mapId = params.get('map');
  if (!mapId || !env.hasMap(mapId)) return null;
  const lvl = Math.round(Number(params.get('lvl') ?? '1'));
  const cls = params.get('class');
  return {
    mapId,
    level: Number.isFinite(lvl) ? Math.min(60, Math.max(1, lvl)) : 1,
    classId: cls && env.classes.includes(cls) ? cls : (env.classes[0] ?? 'warden'),
    name: params.get('name') ?? 'Tester',
  };
}

export function bootRoute(profile: Pick<Profile, 'character' | 'mainClassId'>, params: URLSearchParams, env: BootEnv): BootRoute {
  const dbg = parseDebugBoot(params, env);
  if (dbg) return { kind: 'debug', boot: dbg };
  const c = profile.character;
  if (!c) return { kind: 'create', preselect: env.classes.includes(profile.mainClassId) ? profile.mainClassId : (env.classes[0] ?? '') };
  const known = env.hasMap(c.mapId);
  return { kind: 'world', mapId: known ? c.mapId : env.homeMap, pos: known ? c.pos : null, facing: c.facing ?? 0 };
}
